/**
 * AI Overview by Industry 2026 — /studies/ai-overview-by-industry-2026
 *
 * The companion to the CTR study. That one asked what an AI Overview costs a
 * B2B page in clicks; this one asks where AI Overviews actually appear — and
 * finds that the answer is not "everywhere", and not evenly.
 *
 * Every number on this page comes from data/studies/ai-overview-by-industry/,
 * aggregated by scripts/analyse-ai-overview-study.mjs into the generated module
 * imported below. Nothing here is typed by hand: re-run the script after a
 * refresh and the page, the charts and the CSV move together.
 */
import React from "react";
import SiteLayout from "@/components/SiteLayout";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useIsMobile } from "@/hooks/useIsMobile";
import { VIDEO_REVIEW_URL as STRATEGIST_URL } from "@/lib/cta";
import KeyFindings from "@/components/citability/KeyFindings";
import HowToCite from "@/components/citability/HowToCite";
import ChartEmbed from "@/components/citability/ChartEmbed";
import { AI_OVERVIEW_BY_INDUSTRY as D } from "@/lib/studies/aiOverviewByIndustry.generated";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";
const cyan = "#57d7e2";
const teal = "#1a9aa8";

const SITE = "https://www.joinindexed.com";
const PATH = "/studies/ai-overview-by-industry-2026";
const URL = `${SITE}${PATH}`;
const TITLE = "Where AI Overviews Actually Appear: 11 Industries, 317 Keywords";
const PUBLISHED = "2026-09-19";
const UPDATED = D.collectedAt;
const CSV_PATH = "/data/ai-overview-by-industry-2026-q3.csv";

/** Format an ISO date as "19 September 2026". */
function longDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
}

const bothPct = Math.round((D.overall.both / D.overall.keywordsMeasured) * 1000) / 10;
const sitelinkPct = Math.round((D.overall.aiOverviewWithSitelinks / D.overall.aiOverview) * 1000) / 10;
const top = D.byVertical[0];
const bottom = D.byVertical[D.byVertical.length - 1];

/**
 * The findings block. Written as complete sentences carrying one number each,
 * because a finding a writer has to rephrase is a finding a writer gets wrong.
 */
const FINDINGS: string[] = [
  `${D.overall.aiOverviewPct}% of the ${D.overall.keywordsMeasured} US commercial keywords we measured returned an AI Overview between ${longDate(D.serpWindow.from)} and ${longDate(D.serpWindow.to)}.`,
  `AI Overview presence ranged from ${bottom.aiOverviewPct}% in ${bottom.vertical.toLowerCase()} to ${top.aiOverviewPct}% in ${top.vertical.toLowerCase()} — a ${Math.round((top.aiOverviewPct - bottom.aiOverviewPct) * 10) / 10}-point spread across ${D.byVertical.length} industries.`,
  `Not one of the ${D.cuts.localIntent.keywordsMeasured} "near me" or "local" keywords in the sample returned an AI Overview, against ${D.cuts.nonLocalIntent.aiOverviewPct}% of the other ${D.cuts.nonLocalIntent.keywordsMeasured}.`,
  `Only ${D.overall.both} of ${D.overall.keywordsMeasured} keywords (${bothPct}%) showed an AI Overview and a local pack on the same SERP.`,
  `${D.cuts.localIntent.localPackPct}% of those local-intent keywords returned a local pack instead.`,
  `${D.overall.aiOverviewWithSitelinks} of the ${D.overall.aiOverview} AI Overviews we recorded (${sitelinkPct}%) linked out to at least one source.`,
  `Question-phrased keywords returned an AI Overview ${D.cuts.question.aiOverviewPct}% of the time against ${D.cuts.nonQuestion.aiOverviewPct}% for the rest — a gap our sample of ${D.cuts.question.keywordsMeasured} is too small to call meaningful.`,
];

const CHARTS = [
  {
    imageUrl: `${SITE}/images/studies/ai-overview-presence-by-industry-2026-q3.svg`,
    alt: `Horizontal bar chart of AI Overview presence across ${D.byVertical.length} industries, from ${bottom.aiOverviewPct}% in ${bottom.vertical} to ${top.aiOverviewPct}% in ${top.vertical}`,
    caption: `AI Overview presence by industry, Q3 2026 (n=${D.overall.keywordsMeasured} US keywords)`,
  },
  {
    imageUrl: `${SITE}/images/studies/ai-overview-vs-local-pack-2026-q3.svg`,
    alt: "Paired bar chart comparing AI Overview presence and local pack presence for each industry, showing that the two rarely appear together",
    caption: `AI Overview versus local pack presence, by industry (n=${D.overall.keywordsMeasured})`,
  },
  {
    imageUrl: `${SITE}/images/studies/ai-overview-local-intent-2026-q3.svg`,
    alt: `Bar chart comparing AI Overview presence on local-intent keywords (${D.cuts.localIntent.aiOverviewPct}%) against all other keywords (${D.cuts.nonLocalIntent.aiOverviewPct}%)`,
    caption: `AI Overview presence on local-intent keywords versus everything else`,
  },
];

/* ── Sub-components, matching the CTR study's typography ── */

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ color: teal, fontSize: "12px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "12px" }}>
      {children}
    </div>
  );
}

function SectionHeading({ children, isMobile, id }: { children: React.ReactNode; isMobile: boolean; id?: string }) {
  return (
    <h2 id={id} style={{ color: "#111", fontSize: isMobile ? "26px" : "clamp(28px, 3vw, 36px)", fontWeight: 800, fontFamily: ff, lineHeight: 1.15, letterSpacing: "-0.02em", margin: "0 0 20px" }}>
      {children}
    </h2>
  );
}

function Body({ children }: { children: React.ReactNode }) {
  return <p style={{ color: "#333", fontSize: "17px", fontFamily: ff, lineHeight: 1.75, margin: "0 0 20px" }}>{children}</p>;
}

function StatBoxHero({ value, label }: { value: string; label: string }) {
  return (
    <div style={{ flex: 1, padding: "20px 22px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "12px" }}>
      <div style={{ color: cyan, fontSize: "32px", fontWeight: 900, fontFamily: ff, lineHeight: 1, marginBottom: "8px" }}>{value}</div>
      <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "14px", fontFamily: ff, lineHeight: 1.45 }}>{label}</div>
    </div>
  );
}

export default function AiOverviewByIndustry2026() {
  const isMobile = useIsMobile();
  const maxW = "820px";

  useSeoMeta({
    title: "Where AI Overviews Appear: 11 Industries, 317 Keywords — Indexed",
    description: `Original research: ${D.overall.aiOverviewPct}% of ${D.overall.keywordsMeasured} US commercial keywords returned an AI Overview, ranging from ${bottom.aiOverviewPct}% to ${top.aiOverviewPct}% by industry — and none of the local-intent ones did.`,
    canonical: PATH,
    ogType: "article",
    jsonLd: [
      {
        "@type": "ScholarlyArticle",
        headline: TITLE,
        description: `AI Overview presence measured across ${D.overall.keywordsMeasured} US commercial keywords in ${D.byVertical.length} industries, by Indexed.`,
        url: URL,
        datePublished: PUBLISHED,
        dateModified: UPDATED,
        author: { "@type": "Organization", name: "Indexed", url: SITE },
        publisher: { "@type": "Organization", name: "Indexed", url: SITE },
      },
      {
        // Dataset, not just Article: the aggregated CSV is the thing a
        // researcher wants, and this is how they find it.
        "@type": "Dataset",
        name: `AI Overview presence by industry, Q3 2026`,
        description: `Per-industry counts and rates of AI Overview and local pack presence across ${D.overall.keywordsMeasured} US commercial keywords observed between ${D.serpWindow.from} and ${D.serpWindow.to}.`,
        url: URL,
        license: "https://creativecommons.org/licenses/by/4.0/",
        creator: { "@type": "Organization", name: "Indexed", url: SITE },
        temporalCoverage: `${D.serpWindow.from}/${D.serpWindow.to}`,
        spatialCoverage: "United States",
        distribution: [{ "@type": "DataDownload", encodingFormat: "text/csv", contentUrl: `${SITE}${CSV_PATH}` }],
      },
    ],
  });

  return (
    <SiteLayout>
      <div style={{ background: "#fff", minHeight: "100vh" }}>

        {/* ── HERO ── */}
        <div style={{ background: "#0a0a0a", marginTop: "72px" }}>
          <div style={{ maxWidth: "1240px", margin: "0 auto", padding: isMobile ? "40px 20px 36px" : "56px 48px 52px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "32px" }}>
              <a href="/studies" style={{ color: "rgba(255,255,255,0.4)", fontSize: "12px", fontFamily: ff, textDecoration: "none", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>Studies</a>
              <span style={{ color: "rgba(255,255,255,0.2)", fontSize: "12px" }}>/</span>
              <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "12px", fontFamily: ff, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>AI Overviews by Industry</span>
            </div>

            <div style={{ maxWidth: maxW }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px", flexWrap: "wrap" }}>
                <span style={{ display: "inline-block", padding: "5px 12px", borderRadius: "6px", background: "rgba(87,215,226,0.12)", color: cyan, fontSize: "12px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                  Original Research
                </span>
                <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "14px", fontFamily: ff }}>
                  Published {longDate(PUBLISHED)} · Last updated <time dateTime={UPDATED}>{longDate(UPDATED)}</time>
                </span>
              </div>

              <h1 style={{ color: "white", fontSize: isMobile ? "30px" : "clamp(36px, 4vw, 50px)", fontWeight: 900, fontFamily: ff, lineHeight: 1.06, letterSpacing: "-0.03em", marginBottom: "24px" }}>
                {TITLE}
              </h1>

              <p style={{ color: "rgba(255,255,255,0.5)", fontSize: isMobile ? "16px" : "18px", fontFamily: ff, lineHeight: 1.6, marginBottom: "40px" }}>
                AI Overviews are not spreading evenly across the web. Across {D.byVertical.length} industries
                and {D.overall.keywordsMeasured} measured US keywords, presence runs from {bottom.aiOverviewPct}% to {top.aiOverviewPct}% —
                and on the local-intent queries an SMB actually competes for, it is zero.
              </p>

              <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: "14px" }}>
                <StatBoxHero value={`${D.overall.aiOverviewPct}%`} label={`of ${D.overall.keywordsMeasured} keywords returned an AI Overview`} />
                <StatBoxHero value={`${top.aiOverviewPct}%`} label={`in ${top.vertical} — the most exposed industry`} />
                <StatBoxHero value="0%" label={`of the ${D.cuts.localIntent.keywordsMeasured} "near me" keywords we measured`} />
              </div>
            </div>
          </div>
        </div>

        {/* ── BODY ── */}
        <div style={{ maxWidth: "1240px", margin: "0 auto", padding: isMobile ? "0 20px 60px" : "0 48px 80px" }}>
          <article style={{ maxWidth: maxW, margin: "0 auto", paddingTop: "48px" }}>

            <KeyFindings findings={FINDINGS} />

            <div style={{ height: "48px" }} />

            <SectionLabel>What we found</SectionLabel>
            <SectionHeading isMobile={isMobile}>Industry is a proxy. Intent is the mechanism.</SectionHeading>
            <Body>
              The headline number — {D.overall.aiOverviewPct}% of {D.overall.keywordsMeasured} keywords — is the least
              interesting thing in this dataset, because almost nobody competes across all {D.byVertical.length} industries at once.
              What matters is the spread. {top.vertical} keywords returned an AI Overview {top.aiOverviewPct}% of the time.
              {" "}{bottom.vertical} keywords returned one {bottom.aiOverviewPct}% of the time. Those are not the same search
              engine, and a strategy built on the average is built for a SERP neither of them sees.
            </Body>

            <ChartEmbed chart={CHARTS[0]} pageUrl={URL} pageTitle={TITLE} />

            <Body>
              Read down the list and the pattern is not really about industry at all. The three software categories at the
              top share one property: the query has no place attached to it. The two at the bottom — {bottom.vertical.toLowerCase()} and
              legal services — are queries where the searcher wants somebody nearby. Google appears to be making a judgment
              about what kind of answer the query deserves, and a summarized paragraph is not its answer to "who can come and fix this today".
            </Body>

            <SectionLabel>The clearest signal in the data</SectionLabel>
            <SectionHeading isMobile={isMobile}>Local intent and AI Overviews barely coexist</SectionHeading>
            <Body>
              Of the {D.overall.keywordsMeasured} keywords with an observed SERP, {D.overall.both} ({bothPct}%) showed both an AI
              Overview and a local pack. Isolate the keywords carrying explicit local intent — the ones containing "near me"
              or "local" — and the effect is absolute: {D.cuts.localIntent.aiOverviewPct} of {D.cuts.localIntent.keywordsMeasured} returned
              an AI Overview, while {D.cuts.localIntent.localPackPct}% returned a local pack. Every other keyword in the
              sample returned an AI Overview {D.cuts.nonLocalIntent.aiOverviewPct}% of the time.
            </Body>

            <ChartEmbed chart={CHARTS[2]} pageUrl={URL} pageTitle={TITLE} />

            <Body>
              This is a small sample of local-intent phrasings ({D.cuts.localIntent.keywordsMeasured} keywords), so the honest
              reading is "we did not observe one", not "it cannot happen". But the direction is consistent with the
              industry-level numbers, where the two verticals dominated by local packs are also the two with almost no AI
              Overviews at all. For a business competing on{" "}
              <a href="/blog/near-me-searches-how-to-optimise-your-website-and-gbp-to-capture-high-intent-local-traffic" style={{ color: teal }}>"near me" searches</a>,
              the AI Overview is — for now — somebody else's problem.
            </Body>

            <ChartEmbed chart={CHARTS[1]} pageUrl={URL} pageTitle={TITLE} />

            <SectionLabel>What it means</SectionLabel>
            <SectionHeading isMobile={isMobile}>Where the click is at risk, and where it isn't</SectionHeading>
            <Body>
              Our{" "}
              <a href="/studies/ctr-study-2026" style={{ color: teal }}>CTR study</a>{" "}
              measured what an AI Overview costs a B2B page once it appears. This study answers the prior question: whether
              it appears at all. Put together, they say the exposure is concentrated. A B2B software company publishing
              definitional and comparison content is operating in a category where an AI Overview is close to guaranteed —
              {top.aiOverviewPct}% in {top.vertical.toLowerCase()} — and where the{" "}
              <a href="/blog/how-ai-search-engines-decide-what-to-cite" style={{ color: teal }}>question of what gets cited</a>{" "}
              is the whole game. A home services business is not in that fight yet.
            </Body>
            <Body>
              One number is worth holding onto in both cases: {D.overall.aiOverviewWithSitelinks} of
              the {D.overall.aiOverview} AI Overviews we recorded ({sitelinkPct}%) linked out to at least one source.
              Where the AI Overview shows up, it is still handing out links — to whoever it decided to cite.
            </Body>

            {/* ── METHODOLOGY ── */}
            <div style={{ marginTop: "56px", paddingTop: "40px", borderTop: "1px solid rgba(0,0,0,0.1)" }}>
              <SectionLabel>How we did this</SectionLabel>
              <SectionHeading isMobile={isMobile} id="methodology">Methodology</SectionHeading>
              <Body>
                <strong>Sample.</strong> {D.byVertical.length} industries, each defined by a single seed term. For each
                seed we took the top 30 US keywords by monthly search volume that contain the seed as a phrase, with a
                floor of 500 monthly searches to exclude noise — {D.overall.keywordsCollected} keywords in total.
              </Body>
              <Body>
                <strong>Data source.</strong> SERP feature composition from the Ahrefs Keywords Explorer API
                (<code>serp_features</code>), country <code>{D.country}</code>. A keyword counts as showing an AI Overview
                when its feature list contains <code>ai_overview</code>, and as showing a local pack when it
                contains <code>local_pack</code>.
              </Body>
              <Body>
                <strong>Date range.</strong> Each keyword carries the date its SERP was last crawled. The observations
                here span {longDate(D.serpWindow.from)} to {longDate(D.serpWindow.to)}; they are not a single snapshot of
                one moment, and AI Overview presence is known to fluctuate.
              </Body>
              <Body>
                <strong>Exclusions.</strong> {D.overall.keywordsCollected - D.overall.keywordsMeasured} of
                the {D.overall.keywordsCollected} keywords had no crawled SERP on record. They are excluded from every
                denominator rather than counted as "no AI Overview" — the absence of an observation is ours, not Google's.
                All percentages here use n={D.overall.keywordsMeasured}.
              </Body>
              <Body>
                <strong>Definitions.</strong> "Local intent" means the keyword text contains "near me" or "local"; this is
                a lexical test, not a judgment about the searcher. "Question-phrased" means the keyword begins with what,
                how, why, when, which, who, is, are, do, does or can.
              </Body>
              <Body>
                <strong>Limitations.</strong> Ahrefs' crawl is a sample of one location and device profile per keyword, so
                personalized and mobile SERPs may differ. Per-industry cells are 25–30 keywords, which is enough to
                separate 0% from 97% and not enough to rank two neighboring verticals against each other. Seed-term
                matching means each vertical is a slice of a market, not the market. And presence is not impact: this
                study says where AI Overviews appear, not what they cost — that is the{" "}
                <a href="/studies/ctr-study-2026" style={{ color: teal }}>CTR study's</a> question.
              </Body>
              <Body>
                <strong>Reproducing it.</strong> The aggregated per-industry data is published as a{" "}
                <a href={CSV_PATH} download style={{ color: teal, fontWeight: 700 }}>CSV download</a>. The raw per-keyword
                pulls and the analysis script are in the repository that builds this site.
              </Body>
            </div>

            {/* ── DATA DOWNLOAD ── */}
            <div style={{ marginTop: "40px", padding: "24px 28px", background: "#f8f9fa", border: "1px solid rgba(0,0,0,0.1)", borderRadius: "12px" }}>
              <div style={{ color: "#111", fontFamily: ff, fontSize: "17px", fontWeight: 800, marginBottom: "8px" }}>Take the data</div>
              <p style={{ color: "#5f6b6d", fontFamily: ff, fontSize: "15px", lineHeight: 1.6, margin: "0 0 16px" }}>
                Per-industry counts and rates for all {D.overall.keywordsMeasured} measured keywords, as CSV.
                Free to use with attribution.
              </p>
              <a href={CSV_PATH} download style={{ display: "inline-block", padding: "10px 20px", background: teal, color: "#fff", fontFamily: ff, fontSize: "14px", fontWeight: 700, borderRadius: "999px", textDecoration: "none" }}>
                Download the CSV
              </a>
            </div>

            <HowToCite title={TITLE} url={PATH} date={UPDATED} />

            {/* ── CHANGELOG ── */}
            <section aria-labelledby="changelog-heading" style={{ marginTop: "48px", paddingTop: "32px", borderTop: "1px solid rgba(0,0,0,0.1)" }}>
              <h2 id="changelog-heading" style={{ color: "#111", fontFamily: ff, fontSize: "13px", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 16px" }}>
                Updates to this study
              </h2>
              <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "10px" }}>
                <li style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "150px 1fr", gap: isMobile ? "2px" : "16px", color: "#333", fontFamily: ff, fontSize: "15px", lineHeight: 1.6 }}>
                  <time dateTime={PUBLISHED} style={{ color: "#5f6b6d", fontWeight: 700, fontSize: "12px", letterSpacing: "0.04em", textTransform: "uppercase", paddingTop: "3px" }}>
                    {longDate(PUBLISHED)}
                  </time>
                  <span>First published. Q3 2026 baseline: {D.overall.keywordsCollected} keywords collected across {D.byVertical.length} industries, {D.overall.keywordsMeasured} with an observed SERP.</span>
                </li>
              </ol>
            </section>

            {/* ── CTA ── */}
            <div style={{ marginTop: "48px", padding: "28px 32px", background: "#0a0a0a", borderRadius: "14px" }}>
              <div style={{ color: "#fff", fontFamily: ff, fontSize: "20px", fontWeight: 800, marginBottom: "10px" }}>
                Want to know which of your keywords are exposed?
              </div>
              <p style={{ color: "rgba(255,255,255,0.55)", fontFamily: ff, fontSize: "15px", lineHeight: 1.6, margin: "0 0 18px" }}>
                We run this analysis against your own keyword set — which terms trigger an AI Overview, who gets cited on
                them, and what that is costing you.
              </p>
              <a href={STRATEGIST_URL} style={{ display: "inline-block", padding: "11px 22px", background: cyan, color: "#08272b", fontFamily: ff, fontSize: "15px", fontWeight: 800, borderRadius: "999px", textDecoration: "none" }}>
                Talk to a strategist
              </a>
            </div>
          </article>
        </div>
      </div>
    </SiteLayout>
  );
}
